import Alert, { type AlertColor } from "@mui/material/Alert";
import Snackbar, {
  type SnackbarCloseReason,
  type SnackbarProps,
} from "@mui/material/Snackbar";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useMemo,
  useState,
} from "react";

export interface Toast extends SnackbarProps {
  id: string;
  severity?: AlertColor;
}

interface ToastContextInterface {
  toasts: Toast[];
  setToasts: Dispatch<SetStateAction<any[]>>;
}

export const ToastContext = createContext<ToastContextInterface>({
  toasts: [],
  setToasts: () => {},
});

interface ToasterProps {
  children: ReactNode;
}

export function Toaster({ children }: ToasterProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const value = useMemo<ToastContextInterface>(
    () => ({
      toasts,
      setToasts,
    }),
    [toasts]
  );

  function onClose(_: any, reason: SnackbarCloseReason) {
    if (reason === "clickaway") {
      return;
    }
    setToasts((currentToasts) => {
      const [firstToast, ...rest] = currentToasts;
      if (!firstToast) {
        return [];
      }
      return [{ ...firstToast, open: false }, ...rest];
    });
  }

  function onExited() {
    setToasts((currentToasts) => {
      const newToastList = currentToasts.slice(1);
      const [openToast, ...rest] = newToastList;
      if (!openToast) {
        return [];
      }
      return [{ ...openToast, open: true }, ...rest];
    });
  }

  const { id, severity, message, ...currentProperties } = toasts[0] || {};
  const autoHideDuration = 3000;
  return (
    <ToastContext.Provider value={value}>
      {!severity ? (
        <Snackbar
          key={id}
          autoHideDuration={autoHideDuration}
          message={message}
          {...currentProperties}
          onClose={onClose}
          TransitionProps={{ onExited }}
        />
      ) : (
        <Snackbar
          key={id}
          autoHideDuration={autoHideDuration}
          {...currentProperties}
          onClose={onClose}
          TransitionProps={{ onExited }}
        >
          <Alert severity={severity} data-testid={`${severity}-toast`}>
            {message}
          </Alert>
        </Snackbar>
      )}
      {children}
    </ToastContext.Provider>
  );
}
